import {
    _get, _getNull, decodeUrl,
    getSingleAsync,
    isUndefined,
    showLoading,
    showModal,
    showToast,
    showToastAsync,
    sleep
} from "@utils/utils";
import { sessionStorage } from "@utils/storage";
import QQMapWX from './qqmap-wx-jssdk.js';
import config from "@/config";
import { reportReportPosition } from "@server/report";
import { areaDefaultCity } from "@server/areaApi";
import { useLocationStore } from "@/store/location";
import pinia from "@/store/pinia";
import { commonMpSignature } from "@server/commonApi";
import jweixin from "weixin-js-sdk";
import { initJsSdkConfig } from "@utils/officialAccount";
import { positionGeocoder } from "@server/positionApi";
import { isWxH5 } from "@utils/business";

const locationStore = useLocationStore(pinia);
const QQMap = new QQMapWX({
    key: config.qqMap.key,
});

/**
 * 获取定位坐标
 * @returns {Promise<unknown>}
 */
export const _getLocation = () => {
    return new Promise(resolve => {
        // #ifdef MP-WEIXIN
        uni.getLocation({
            type: 'gcj02',
            // isHighAccuracy: true,
            success: (e) => {
                if (e.errMsg.indexOf('ok') > -1) {
                    return resolve({ code: 0, ...e })
                }
                console.log(e, '_getLocation:success')
                resolve({ code: -5, msg: '定位失败，请检查微信是否开启定位权限' })
            },
            fail: (err) => {
                if (err.errMsg.indexOf('system permission denied') > -1) {
                    return resolve({ code: -4, msg: '定位失败，请检查系统是否开启微信定位权限' })
                }
                console.error(err, '_getLocation:fail');
                resolve({ code: -6, msg: '获取定位信息失败' })
            }
        })
        // #endif


        // #ifdef H5
        initJsSdkConfig(['getLocation']).then(res => {
            res.getLocation({
                type: 'gcj02',
                complete(e) {
                    console.error('getLocation', e)
                    if (e.errMsg.indexOf('ok') > -1) {
                        return resolve({ code: 0, ...e })
                    }
                    resolve({ code: -6, msg: '获取定位信息失败' })
                }
            });
        })
        // #endif
    })
}

/**
 * 打开设置页
 * @returns {Promise<unknown>}
 */
const _openSettingPage = () => {
    return new Promise(resolve => {
        uni.openSetting({
            success(res) {
                // 开启定位权限
                if (res.authSetting['scope.userLocation']) {
                    return resolve({ code: 0, msg: '' })
                }
                resolve({ code: -1, msg: '定位权限开启失败' })
            },
            fail: () => {
                // 调取api失败，认为有定位权限
                resolve({ code: 0, msg: '定位权限未知' })
            }
        })
    })
}


/**
 * 校验当前是否有定位权限
 * @returns {Promise<unknown>}
 */
export const _authLocationPermission = () => {
    return new Promise(resolve => {
        // #ifdef MP-WEIXIN
        uni.getSetting({
            success: (res) => {
                console.log(res);
                // 用户已拒绝
                if (res.errMsg.indexOf('auth deny') > -1) {
                    return resolve({ code: -1, msg: '定位权限被拒绝' })
                }

                // 未知权限，此时用户还没有允许或者拒绝授权
                if (isUndefined(res.authSetting['scope.userLocation'])) {
                    return resolve({ code: -2, msg: '定位权限未知' })
                }

                // 拒绝定位权限
                if (res.authSetting['scope.userLocation'] === false) {
                    return resolve({ code: -3, msg: '定位权限已拒绝' })
                }

                // 允许定位
                if (res.authSetting['scope.userLocation']) {
                    return resolve({ code: 0 })
                }
                resolve(true)
            },
            fail: () => {
                resolve(true)
            }
        })
        // #endif

        // #ifdef H5
        return resolve({ code: 0 })
        // #endif
    })
}

export const getLocationByCache = async () => {
    let locationInfo = sessionStorage.get('__location');
    if (locationInfo) return locationInfo;
    locationInfo = await getLocation();
    if (locationInfo) {
        // 缓存10分钟
        sessionStorage.set('__location', locationInfo, { duration: 60 * 10 });
    }
    locationStore.setLocation(locationInfo)
    return locationInfo;
}


/**
 * 获取定位信息，有定位权限返回定位信息，否则返回null
 * @returns {Promise<*|null>}
 */
export const getLocationWhenHasPermission = async () => {
    // 判断定位权限
    const permissionRes = await _authLocationPermission();
    if (permissionRes.code !== 0) return null;
    return await getLocationByCache();
}

// 1. 定位流程，直接调用 getLocation
// 2. 先判断有没有权限，如果位置，需要去调用接口
// 2. 根据接口返回结果，弹窗引导 toast 或 modal
// 3. 如引导打开系统设置、或打开小程序设置页面

export const getLocation = async () => {
    // 判断定位权限
    const permissionRes = await _authLocationPermission();
    const getLocationInfo = async () => {
        const res = await _getLocation();
        if (res.code === 0) {
            return res;
        }
        await showToastAsync(res.msg)
        return null;
    }

    // 允许定位 || 未知定位权限
    if ([0, -2].includes(permissionRes.code)) {
        // 获取定位
        return await getLocationInfo();
    }

    // 用户拒绝定位
    if ([-1, -3].includes(permissionRes.code)) {
        // 引导弹窗
        const modalE = await showModal({
            title: '提示',
            content: '当前无定位权限，是否去开启定位？',
        })
        if (!modalE || !modalE.confirm) return null;
        // 打开开启授权页
        const settingRes = await _openSettingPage();
        // 判断授权页是否已同意授权
        if (settingRes.code !== 0) {
            await showToastAsync(settingRes.msg)
            return null;
        }
        // 同意授权，重新获取定位
        return await getLocationInfo();
    }
}

export const getCityInfoByLocation = async (location) => {
    return new Promise(async resolve => {
        QQMap.reverseGeocoder({
            location: {
                latitude: location.latitude,
                longitude: location.longitude,
            },
            sig: config.qqMap.sig,
            success: (res) => {
                if (res.message !== "Success") {
                    return resolve(null)
                }
                const cityInfo = res.result.ad_info;
                resolve({
                    name: cityInfo.city,
                    code: cityInfo.city_code.split(cityInfo.nation_code)[1],
                    cityInfo,
                })
            },
            fail: (res) => {
                console.error('getCityInfoByLocation', res);
                resolve(null)
            },
        })
    })
}

export const getWxH5CityInfoByLocation = async (location) => {
    return new Promise(async resolve => {
        const res = await positionGeocoder({
            lat: location.latitude,
            lon: location.longitude,
        })
        if (_getNull(res, 'data.message') !== "Success") {
            return resolve(null)
        }
        const cityInfo = res.data.result.ad_info;
        resolve({
            name: cityInfo.city,
            code: cityInfo.city_code.split(cityInfo.nation_code)[1],
            cityInfo,
        })
    })
}

/**
 * 根据定位坐标合并城市数据
 * @param location
 * @returns {null | Object}
 */
export const mergeCityByLocation = async (location) => {
    return new Promise(async resolve => {

        let cityInfo = null;
        if (isWxH5()) {
            cityInfo = await getWxH5CityInfoByLocation(location);
        } else {
            cityInfo = await getCityInfoByLocation(location);
        }

        const defaultCity = await areaDefaultCity(_get(cityInfo, 'cityInfo.adcode', ''));
        resolve(
            defaultCity.$success
                ? {
                    ...(cityInfo || {}),
                    name: defaultCity.data.cityName,
                    code: defaultCity.data.cityCode
                }
                : cityInfo
        );
    })
}

/**
 * 根据定位 获取汇总数据
 * @returns {Promise<unknown>}
 */
export const getSummaryByLocation = () => {
    return new Promise(async resolve => {
            const hideLoading = showLoading('定位中')
            const location = await getLocationByCache();
            hideLoading();
            if (!location) {
                // 采用后端接口兜底
                const defaultCity = await areaDefaultCity('');
                if (!defaultCity.$success) {
                    await showToastAsync('经纬度解析失败~');
                    resolve(null)
                }
                const cityInfo = {
                    name: defaultCity.data.cityName,
                    code: defaultCity.data.cityCode
                };
                locationStore.setLocationCity(cityInfo)
                resolve(cityInfo)
                return;
            }
            const cityInfo = await mergeCityByLocation(location);
            if (!cityInfo) {
                await showToastAsync('经纬度解析失败~');
                resolve(null)
                return;
            }
            locationStore.setLocationCity(cityInfo)
            reportPosition({
                location,
                cityInfo
            });
            resolve(cityInfo)
        }
    )
}

/**
 * 上报地理位置
 * @param data
 */
export const reportPosition = (data) => {
    try {
        const { latitude, longitude } = data.location;
        const { cityInfo, name, code } = data.cityInfo;
        reportReportPosition({
            // ...cityInfo,
            lat: latitude,
            lon: longitude,
            cityCode: cityInfo.adcode,
        })
    } catch (e) {

    }
}

/**
 * 打开地图选点
 * @returns {Promise<unknown>}
 */
export const chooseLocation = () => {
    return new Promise(resolve => {
        uni.chooseLocation({
            success: (res) => {
                res.code = 0;
                resolve(res)
            },
            fail: (error) => {
                console.error(error)
                // 用户主动取消
                if (error.errMsg.indexOf('cancel') > -1) {
                    return resolve({ code: -1 })
                }
                resolve({ code: -2 })
            }
        });
    })
}

/**
 * 根据经纬度打开地图
 * @param lat
 * @param lon
 */
export const openLocation = ({ lat, lon, name }) => {
    uni.openLocation({
        latitude: lat,
        longitude: lon,
        name: decodeUrl(name),
        success: () => {
            console.log('success');
        },
        fail: (e) => {
            console.error(e)
        },
    });
}


// 定义一些常量
const pi = 3.14159265358979324;
const a = 6378245.0;
const ee = 0.00669342162296594323;

// 判断是否在中国境内
function outOfChina(lat, lon) {
    if (lon < 72.004 || lon > 137.8347) {
        return true;
    }
    if (lat < 0.8293 || lat > 55.8271) {
        return true;
    }
    return false;
}

// 转换经度
function transformLon(x, y) {
    let ret =
        300.0 +
        x +
        2.0 * y +
        0.1 * x * x +
        0.1 * x * y +
        0.1 * Math.sqrt(Math.abs(x));
    ret +=
        ((20.0 * Math.sin(6.0 * x * pi) +
                20.0 * Math.sin(2.0 * x * pi)) *
            2.0) /
        3.0;
    ret +=
        ((20.0 * Math.sin(x * pi) + 40.0 * Math.sin((x / 3.0) * pi)) * 2.0) / 3.0;
    ret +=
        ((150.0 * Math.sin((x / 12.0) * pi) +
                300.0 * Math.sin((x / 30.0) * pi)) *
            2.0) /
        3.0;
    return ret;
}

// 转换纬度
function transformLat(x, y) {
    let ret =
        -100.0 +
        2.0 * x +
        3.0 * y +
        0.2 * y * y +
        0.1 * x * y +
        0.2 * Math.sqrt(Math.abs(x));
    ret +=
        ((20.0 * Math.sin(6.0 * x * pi) +
                20.0 * Math.sin(2.0 * x * pi)) *
            2.0) /
        3.0;
    ret +=
        ((20.0 * Math.sin(y * pi) + 40.0 * Math.sin((y / 3.0) * pi)) * 2.0) / 3.0;
    ret +=
        ((160.0 * Math.sin((y / 12.0) * pi) +
                320 * Math.sin((y * pi) / 30.0)) *
            2.0) /
        3.0;
    return ret;
}

// WGS84坐标系转GCJ02坐标系
export const wgs84ToGcj02 = (wgsLat, wgsLon) => {
    if (outOfChina(wgsLat, wgsLon)) {
        return [wgsLat, wgsLon];
    }
    let dLat = transformLat(wgsLon - 105.0, wgsLat - 35.0);
    let dLon = transformLon(wgsLon - 105.0, wgsLat - 35.0);
    const radLat = (wgsLat / 180.0) * pi;
    let magic = Math.sin(radLat);
    magic = 1 - ee * magic * magic;
    const sqrtMagic = Math.sqrt(magic);
    dLat = (dLat * 180.0) / (((a * (1 - ee)) / (magic * sqrtMagic)) * pi);
    dLon = (dLon * 180.0) / ((a / sqrtMagic) * Math.cos(radLat) * pi);
    const gcjLat = wgsLat + dLat;
    const gcjLon = wgsLon + dLon;
    return { latitude: gcjLat, longitude: gcjLon };
}

// GCJ02坐标系转WGS84坐标系
export const gcj02ToWgs84 = (gcjLat, gcjLon) => {
    if (outOfChina(gcjLat, gcjLon)) {
        return [gcjLat, gcjLon];
    }
    let dLat = transformLat(gcjLon - 105.0, gcjLat - 35.0);
    let dLon = transformLon(gcjLon - 105.0, gcjLat - 35.0);
    const radLat = (gcjLat / 180.0) * pi;
    let magic = Math.sin(radLat);
    magic = 1 - ee * magic * magic;
    const sqrtMagic = Math.sqrt(magic);
    dLat = (dLat * 180.0) / (((a * (1 - ee)) / (magic * sqrtMagic)) * pi);
    dLon = (dLon * 180.0) / ((a / sqrtMagic) * Math.cos(radLat) * pi);
    const wgsLat = gcjLat - dLat;
    const wgsLon = gcjLon - dLon;
    return { latitude: wgsLat, longitude: wgsLon };
}
